import { Button, Divider } from '@dsg-tech/homefield-react';
import useLogger from 'hooks/useLogger';
import {
  HomefieldIconAppleSignIn,
  HomefieldIconGoogleSignIn,
  HomefieldIconStarPointer,
} from '@dsg-tech/homefield-react-icons';
import { KEY_CODES } from 'utility/constants';
import SignInButton from './SignInButton';

const SignedOutState = () => {
  const { error } = useLogger();
  const handleLogin = (signIn?: string) => {
    const authFunctionsMap: { [key: string]: () => Promise<void> } = {
      join: window.authFunctions.createAccount,
      google: window.authFunctions.loginWithGoogle,
      apple: window.authFunctions.loginWithApple,
      default: window.authFunctions.login,
    };

    const authFunction = authFunctionsMap[signIn ?? 'default'];

    authFunction().catch((e: any) => {
      error('SignedOutState', { ErrorMessage: 'Could not login user', Error: e });
    });
  };

  return (
    <div className="hmf-px-m hmf-py-s my-account-signed-out" data-testid="signed-out-state">
      <div className="sign-in-button-container">
        <Button
          variant="primary"
          label="Sign in to my account"
          text="SIGN IN"
          className="sign-in-button hmf-py-s hmf-px-l"
          onClick={() => {
            handleLogin();
          }}
          onKeyDown={(event) => {
            if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
              handleLogin();
            }
          }}
        />
        <div className="hmf-mt-xxs">
          <Button
            variant="secondary"
            label="Join now"
            text="JOIN NOW"
            className="sign-in-button hmf-py-s hmf-px-l"
            onClick={() => {
              handleLogin('join');
            }}
            onKeyDown={(event) => {
              if (event.key === KEY_CODES.SPACE || event.key === KEY_CODES.ENTER) {
                handleLogin('join');
              }
            }}
          />
        </div>
      </div>
      <div className="hmf-display-flex hmf-align-items-center hmf-my-m divider-container hmf-justify-content-between hmf-w-100">
        <Divider topMargin="xs" bottomMargin="xs" />
        <span className="hmf-px-xxs hmf-body-bold-s hmf-text-align-center label-base-subtle">
          or continue with
        </span>
        <Divider topMargin="xs" bottomMargin="xs" />
      </div>
      <div className="hmf-display-flex sign-in-button-container">
        <SignInButton
          label="Sign in with Google"
          icon={<HomefieldIconGoogleSignIn height={20} width={20} />}
          onClickHandler={() => {
            handleLogin('google');
          }}
        />
        <span className="hmf-ml-xxs" />
        <SignInButton
          label="Sign in with Apple"
          icon={<HomefieldIconAppleSignIn height={20} width={20} />}
          onClickHandler={() => {
            handleLogin('apple');
          }}
        />
      </div>
      <p className="hmf-body-s hmf-mt-xs hmf-mb-s label-base-subtle policy-container">
        By clicking to continue with Google or Apple above, I am agreeing to the{' '}
        <a
          href="https://www.dickssportinggoods.com/s/policy/terms-of-use"
          target="_blank"
          aria-label="Navigate to Terms of use policy">
          DICK&apos;S Terms of Use
        </a>
        ,{' '}
        <a
          href="https://www.dickssportinggoods.com/s/policy/privacy-policy"
          target="_blank"
          aria-label="Navigate to privacy policy">
          Privacy Policy
        </a>{' '}
        and{' '}
        <a
          href="https://www.dickssportinggoods.com/s/score-card-terms"
          target="_blank"
          aria-label="Navigate to Scorecard terms and conditions">
          ScoreCard Terms and Conditions
        </a>
        {/* */}.
      </p>
      <section className="hmf-py-s information-container">
        <div className="hmf-mb-s">
          <div className="hmf-display-flex hmf-align-items-center">
            <HomefieldIconStarPointer width={20} height={20} />
            <p className="hmf-body-bold-m hmf-pl-xxxs hmf-m-0 label-base">
              Earn Points on Your Purchase
            </p>
          </div>
          <p className="hmf-body-s hmf-m-0 hmf-pt-xxxs label-base-subtle">
            Earn 1 pt for every $1 spent. 300 Pts = $10 Reward!
          </p>
        </div>
        <div className="hmf-mb-s">
          <div className="hmf-display-flex hmf-align-items-center">
            <HomefieldIconStarPointer width={20} height={20} />
            <p className="hmf-body-bold-m hmf-pl-xxxs hmf-m-0 label-base">Free Shipping</p>
          </div>
          <p className="hmf-body-s hmf-m-0 hmf-pt-xxxs label-base-subtle">
            On orders $49 or more.{' '}
            <a
              href="https://www.dickssportinggoods.com/s/clearance-promo/shipping-value-promo-details"
              target="_blank"
              className="hmf-text-decoration-underline label-base-subtle"
              aria-label="Navigate to Shipping details">
              Details
            </a>
            {/* */}.
          </p>
        </div>
        <div className="hmf-mb-s">
          <div className="hmf-display-flex hmf-align-items-center">
            <HomefieldIconStarPointer width={20} height={20} />
            <p className="hmf-body-bold-m hmf-pl-xxxs hmf-m-0 label-base">
              Fast Checkout & Easy Order Tracking
            </p>
          </div>
          <p className="hmf-body-s hmf-m-0 hmf-pt-xxxs label-base-subtle">
            Save your preferences and stay updated on orders.
          </p>
        </div>
        <div className="hmf-mb-s">
          <div className="hmf-display-flex hmf-align-items-center">
            <HomefieldIconStarPointer width={20} height={20} />
            <p className="hmf-body-bold-m hmf-pl-xxxs hmf-m-0 label-base">
              Exclusive Access to Products & Offers
            </p>
          </div>
          <p className="hmf-body-s hmf-m-0 hmf-pt-xxxs label-base-subtle">
            Redeem ScoreCard member-only offers.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SignedOutState;
